import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Truck, AlertCircle, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (user) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    const target = res.user.role === "admin" ? "/admin" : "/select-profile";
    navigate(loc.state?.from?.pathname || target, { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ background: "var(--lc-surface)" }}>
      <div className="w-full max-w-[400px]">
        {/* Brand */}
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div
            className="w-10 h-10 flex items-center justify-center rounded-xl"
            style={{ background: "var(--lc-orange)", boxShadow: "0 4px 12px rgba(255, 90, 31, 0.25)" }}
          >
            <Truck className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="display font-bold text-base">LC Services</div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--lc-gray)]">Livraison Cuisine</div>
          </div>
        </div>

        {/* Card */}
        <div className="lc-card p-7" data-testid="login-card">
          <h1 className="display text-2xl font-bold mb-1.5">Bienvenue</h1>
          <p className="text-sm text-[var(--lc-gray)] mb-6">Connectez-vous à votre espace.</p>

          {error && (
            <div
              data-testid="login-error"
              className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="lc-label" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                data-testid="login-email-input"
                type="email"
                className="lc-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="vous@entreprise.fr"
              />
            </div>

            <div>
              <label className="lc-label" htmlFor="login-pwd">Mot de passe</label>
              <input
                id="login-pwd"
                data-testid="login-password-input"
                type="password"
                className="lc-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="lc-btn-primary lc-btn-lg w-full mt-2"
            >
              {loading ? "Connexion…" : (<>Se connecter <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-[var(--lc-gray)]">
          Pas de compte ? Les accès sont créés par l'administrateur.
        </p>
      </div>
    </div>
  );
}
